import {
  Button,
  CardContent,
  FormControl,
  FormLabel,
  TextField,
  FormHelperText,
} from '@mui/material';
import type { SamlIdentityProvider } from '@repo/api-gw-sdk';
import { useEffect, useState } from 'react';

import { useDAL } from '@/contexts/useDAL';
import { useRoles } from '@/contexts/useRoles';

import ConfirmationDialog from '../dialogs/confirmationDialog';
import { PanelWrapper } from '../panels/panelWrapper';

const PROVIDER_NAME_PATTERN =
  '[^_\\p{Z}][\\p{L}\\p{M}\\p{S}\\p{N}\\p{P}][^_\\p{Z}]+';

const providerNameErrorText = `Provider name must satisfy regular expression pattern: ${PROVIDER_NAME_PATTERN}`;

export const SamlPanel = ({
  saml,
  onSave,
  onDelete,
  onClose,
}: {
  saml: SamlIdentityProvider;
  onSave: (saml: SamlIdentityProvider) => void;
  onDelete: (id: string) => void;
  onClose: () => void;
}) => {
  const dal = useDAL();
  const { isAuthorized } = useRoles();
  const [updatedSaml, setUpdatedSaml] = useState(saml);
  const [providerNameError, setProviderNameError] = useState('');
  const [samlToDelete, setSamlToDelete] = useState<SamlIdentityProvider | null>(
    null
  );

  useEffect(() => {
    if (saml.id !== updatedSaml.id) {
      setUpdatedSaml(saml);
      setProviderNameError('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saml]);

  const handleProviderNameChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newValue = event.target.value;
    setUpdatedSaml({
      ...updatedSaml,
      providerName: newValue,
    });

    setProviderNameError(
      event.target.validity.patternMismatch ? providerNameErrorText : ''
    );
  };

  const handleSave = () => {
    // Check if provider name is valid before saving
    if (providerNameError) {
      return;
    }

    if (updatedSaml.id) {
      void dal.saml
        .update(updatedSaml.id, {
          ...updatedSaml,
          groupToRoleMappingList: updatedSaml.groupToRoleMappingList || [],
        })
        .then((newSaml) => onSave(newSaml));
    } else {
      void dal.saml
        .create({
          ...updatedSaml,
          groupToRoleMappingList: updatedSaml.groupToRoleMappingList || [],
        })
        .then((newSaml) => onSave(newSaml));
    }
  };

  return (
    <PanelWrapper
      header={{
        title: updatedSaml.id ? 'Edit SAML' : 'Create SAML',
        onClose,
      }}
    >
      <CardContent className='w-1/2'>
        <FormControl size='small' className='w-full mt-[24px]'>
          <FormLabel>Provider name</FormLabel>
          <TextField
            size='small'
            value={updatedSaml.providerName}
            onChange={handleProviderNameChange}
            error={!!providerNameError}
            inputProps={{
              pattern: PROVIDER_NAME_PATTERN,
              title: providerNameErrorText,
            }}
          />
          {providerNameError && (
            <FormHelperText>{providerNameError}</FormHelperText>
          )}
        </FormControl>
        <FormControl size='small' className='w-full mt-[24px]'>
          <FormLabel>SSO URL</FormLabel>
          <TextField
            size='small'
            value={updatedSaml.ssoUrl}
            onChange={(event) =>
              setUpdatedSaml({
                ...updatedSaml,
                ssoUrl: event.target.value,
              })
            }
          />
        </FormControl>
        <FormControl size='small' className='w-full mt-[24px]'>
          <FormLabel>Issuer URL</FormLabel>
          <TextField
            size='small'
            value={updatedSaml.issuerUrl}
            onChange={(event) =>
              setUpdatedSaml({
                ...updatedSaml,
                issuerUrl: event.target.value,
              })
            }
          />
        </FormControl>
        <FormControl size='small' className='w-full mt-[24px]'>
          <FormLabel>Logout URL</FormLabel>
          <TextField
            size='small'
            value={updatedSaml.logoutUrl}
            onChange={(event) =>
              setUpdatedSaml({
                ...updatedSaml,
                logoutUrl: event.target.value,
              })
            }
          />
        </FormControl>
        <FormControl size='small' className='w-full mt-[24px]'>
          <FormLabel>Certificate</FormLabel>
          <TextField
            multiline
            sx={{ textarea: { padding: 0 } }}
            size='small'
            value={updatedSaml.certificate}
            onChange={(event) =>
              setUpdatedSaml({
                ...updatedSaml,
                certificate: event.target.value,
              })
            }
          />
        </FormControl>
        <Button
          disabled={!isAuthorized('update:idp_configs') || !!providerNameError}
          variant='outlined'
          className='mt-[24px]'
          onClick={handleSave}
        >
          Save
        </Button>
        {updatedSaml.id && isAuthorized('delete:idp_configs') && (
          <Button
            variant='outlined'
            className='mt-[24px] ml-[8px]'
            onClick={() => {
              setSamlToDelete(updatedSaml);
            }}
          >
            Delete
          </Button>
        )}
      </CardContent>
      <ConfirmationDialog
        isOpen={!!samlToDelete}
        title={`Delete SAML configuration`}
        description={
          <>
            Are you sure you want to delete{' '}
            <span className='font-semibold'>{samlToDelete?.providerName}</span>{' '}
            SAML configuration?
          </>
        }
        buttonTitle='Delete'
        onCancel={() => setSamlToDelete(null)}
        onConfirm={async () => {
          if (!samlToDelete) {
            throw new Error('Cannot find SAML configuration');
          }
          await dal.saml.delete(samlToDelete.id);
          onDelete(samlToDelete.id);
        }}
        confirmSnackbarText={`${samlToDelete?.providerName} has been deleted.`}
      />
    </PanelWrapper>
  );
};
